import {
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SEED_DRESS,
  SUMMARY_ENTITY_SOIL_INPUT,
} from 'constants/uiConstants';
import { InputStepType } from 'interfaces/IInputStep';
import type { SummaryEntityCategory } from '../../../../redux/slices/InsightsSlice';

/**
 * The one set of cost categories used by every view (Live totals, Insights,
 * Diagnostics), in display order. Seed dress is part of Seed here.
 */
export const COST_CATEGORY_ORDER: SummaryEntityCategory[] = [
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
];

/** Step types that carry rate and cost (extend IInputStepRateCost). */
export const COST_STEP_TYPES = new Set<InputStepType>([
  InputStepType.Application,
  InputStepType.CropProtection,
  InputStepType.Adjuvant,
  InputStepType.SoilInput,
  InputStepType.Biostimulant,
  InputStepType.Seed,
  InputStepType.SeedDress,
]);

/** Step type → cost category. Seed dress counts as Seed; CropProtection and Adjuvant count as Plant Protection. */
export const STEP_TYPE_TO_COST_CATEGORY: Partial<
  Record<InputStepType, SummaryEntityCategory>
> = {
  [InputStepType.Seed]: SUMMARY_ENTITY_SEED,
  [InputStepType.SeedDress]: SUMMARY_ENTITY_SEED,
  [InputStepType.SoilInput]: SUMMARY_ENTITY_SOIL_INPUT,
  [InputStepType.CropProtection]: SUMMARY_ENTITY_PLANT_PROTECTION,
  [InputStepType.Adjuvant]: SUMMARY_ENTITY_PLANT_PROTECTION,
  [InputStepType.Biostimulant]: SUMMARY_ENTITY_BIOSTIMULANT,
  [InputStepType.Application]: SUMMARY_ENTITY_OPERATION,
};

/**
 * Quantity groupings (Quantity required, Input summaries) keep Seed dress
 * separate: its rate is per 100 kg of seed, so it can't be added to seed kg.
 */
export const QUANTITY_CATEGORY_ORDER: SummaryEntityCategory[] = [
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SEED_DRESS,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
];

export const STEP_TYPE_TO_QUANTITY_CATEGORY: Partial<
  Record<InputStepType, SummaryEntityCategory>
> = {
  ...STEP_TYPE_TO_COST_CATEGORY,
  [InputStepType.SeedDress]: SUMMARY_ENTITY_SEED_DRESS,
};

export interface CostLineLike {
  stepType: InputStepType;
  totalCost: number;
  costPerHa: number;
  stepCount: number;
}

export interface CategoryCostLine {
  category: SummaryEntityCategory;
  totalCost: number;
  costPerHa: number;
  stepCount: number;
}

/** Fold per-step-type cost lines into the shared cost categories, in COST_CATEGORY_ORDER. Categories with no cost are omitted. */
export const groupCostLinesByCategory = (
  lines: CostLineLike[]
): CategoryCostLine[] => {
  const acc = new Map<SummaryEntityCategory, CategoryCostLine>();
  for (const line of lines) {
    const category = STEP_TYPE_TO_COST_CATEGORY[line.stepType];
    if (!category) continue;
    const existing = acc.get(category);
    if (existing) {
      existing.totalCost += line.totalCost;
      existing.costPerHa += line.costPerHa;
      existing.stepCount += line.stepCount;
    } else {
      acc.set(category, {
        category,
        totalCost: line.totalCost,
        costPerHa: line.costPerHa,
        stepCount: line.stepCount,
      });
    }
  }
  return COST_CATEGORY_ORDER.flatMap((c) => {
    const line = acc.get(c);
    return line ? [line] : [];
  });
};
